import * as yup from 'yup';
import { cloneDeep, sortBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { type Contact } from '@/types/trpc/contact';
import { type EmailAddressValue } from '~/common/types/email/emailValue';
import { type IbanFormikValue } from '@/types/finance';
import { type PhoneNumberData } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.types';

import { type CaseRole, Country, Gender, Language } from '~/common/enums';
import EmailContext from '~/common/enums/emailContext';

import { getInitialPhoneNumbers } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.utils';

import { usePhoneNumbersSchema } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.hooks';

import {
  hasAtMostOneDefaultIban,
  hasDefaultIban,
  hasNoDuplicateIbans,
} from '@/shared/utils/ibanListRules';

export type ContactFormValues = {
  addressLine: string | null;
  city: string | null;
  contactId: string | null;
  contactTags: string[] | null;
  country: Country | null;
  defaultCaseRole: CaseRole | null;
  emailAddresses: EmailAddressValue[];
  firstName: string | null;
  gender: Gender | null;
  ibans: IbanFormikValue[];
  language: Language | null;
  lastName: string | null;
  note: string | null;
  phoneNumbers: PhoneNumberData[];
  postalCode: string | null;
  pushCrmChanges: boolean;
};

type Input = {
  initialContact?: Contact;
};

const DEFAULT_CONTACT_VALUES: ContactFormValues = {
  addressLine: '',
  city: '',
  contactId: null,
  contactTags: [],
  country: Country.BE,
  defaultCaseRole: null,
  emailAddresses: [
    {
      email: '',
      emailContext: [EmailContext.All],
    },
  ],
  firstName: '',
  gender: null,
  ibans: [],
  language: Language.Dutch,
  lastName: '',
  note: null,
  phoneNumbers: [],
  postalCode: '',
  pushCrmChanges: false,
};

const getInitialContactTags = (contact: Contact) => contact.contactTags.map((tag) => tag.tagId);

const getInitialIBANs = (contact: Contact) => {
  if (!contact.ibans) return undefined;

  const withKeys: IbanFormikValue[] = contact.ibans.map((iban) => ({
    accountNumber: iban.accountNumber,
    bic: iban.bic,
    isDefault: iban.isDefault,
    tempKey: uuidv4(),
  }));

  return sortBy(withKeys, (iban) => !iban.isDefault);
};

const useContactForm = (input?: Input) => {
  const { initialContact } = input ?? {};

  const { t } = useTranslation('errors');

  const phoneNumbersSchema = usePhoneNumbersSchema();

  const emailAddressSchema = useMemo(
    () =>
      yup.object({
        email: yup.string().nullable().email(t('invalidEmail')),
        emailContext: yup.array(yup.number()),
      }),
    [t],
  );

  const ibanSchema = useMemo(
    () =>
      yup.object({
        accountNumber: yup.string().nullable().required(t('fieldIsRequired')).accountNumber(),
        bic: yup.string().nullable().required(t('fieldIsRequired')).bic(),
        isDefault: yup.boolean().required(t('fieldIsRequired')),
      }),
    [t],
  );

  const initialValues: ContactFormValues = useMemo(() => {
    const defaultValues = cloneDeep(DEFAULT_CONTACT_VALUES);

    if (!initialContact) return defaultValues;

    return {
      addressLine: initialContact.address?.addressLine ?? defaultValues.addressLine,
      city: initialContact.address?.city ?? defaultValues.city,
      contactId: initialContact.id,
      contactTags: getInitialContactTags(initialContact),
      country: initialContact.address?.country
        ? (initialContact.address.country as Country)
        : defaultValues.country,
      defaultCaseRole: initialContact.defaultCaseRole
        ? (initialContact.defaultCaseRole as CaseRole)
        : defaultValues.defaultCaseRole,
      emailAddresses: initialContact.emailAddresses,
      firstName: initialContact.firstName ?? defaultValues.firstName,
      gender: Gender[initialContact.gender],
      ibans: getInitialIBANs(initialContact) ?? defaultValues.ibans,
      language: Language[initialContact.language],
      lastName: initialContact.lastName ?? defaultValues.lastName,
      note: initialContact.note ?? defaultValues.note,
      phoneNumbers: getInitialPhoneNumbers(initialContact) ?? defaultValues.phoneNumbers,
      postalCode: initialContact.address?.postalCode ?? defaultValues.postalCode,
      pushCrmChanges: defaultValues.pushCrmChanges,
    };
  }, [initialContact]);

  const validationSchema = useMemo(() => {
    const baseSchema = yup.object({
      addressLine: yup.string().nullable(),
      city: yup.string().nullable(),
      defaultCaseRole: yup.string().nullable(),
      emailAddresses: yup.array(emailAddressSchema),
      firstName: yup.string().nullable(),
      gender: yup.string().nullable(),
      ibans: yup
        .array(ibanSchema)
        .test('hasDefault', t('defaultValueIsRequired'), hasDefaultIban)
        .test('hasOnlyOneDefault', t('onlyOneDefaultValueIsAllowed'), hasAtMostOneDefaultIban)
        .test('hasNoDuplicateIbans', t('ibanAlreadyAdded'), hasNoDuplicateIbans),
      language: yup.string().nullable(),
      lastName: yup.string().nullable().required(t('fieldIsRequired')),
      postalCode: yup.string().nullable(),
      pushCrmChanges: yup.boolean(),
    });

    return phoneNumbersSchema.extend(baseSchema);
  }, [emailAddressSchema, ibanSchema, phoneNumbersSchema, t]);

  return useMemo(
    () => ({
      initialValues,
      validationSchema,
    }),
    [initialValues, validationSchema],
  );
};

export default useContactForm;
